import React from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Slider,
  Paper,
  Grid,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { getAnalytics, logEvent } from 'firebase/analytics';

const AccessibilitySettings = ({ 
  highContrastMode = false, 
  largeButtonsMode = false,
  onHighContrastChange,
  onLargeButtonsChange,
}) => {
  // Handle high contrast mode toggle
  const handleHighContrastChange = (event) => {
    if (onHighContrastChange) {
      onHighContrastChange(event.target.checked);
      
      // Log accessibility change to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'accessibility_setting_change', {
          calculator_type: 'scientific',
          setting: 'high_contrast',
          value: event.target.checked ? 'on' : 'off',
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    }
  };

  // Handle large buttons mode toggle
  const handleLargeButtonsChange = (event) => {
    if (onLargeButtonsChange) {
      onLargeButtonsChange(event.target.checked);
      
      // Log accessibility change to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'accessibility_setting_change', {
          calculator_type: 'scientific',
          setting: 'large_buttons',
          value: event.target.checked ? 'on' : 'off',
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Accessibility Settings
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        These settings help make the calculator more accessible for users with different needs.
      </Alert>
      
      <Grid container spacing={3}>
        {/* Visual Settings */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Brightness4Icon sx={{ mr: 1 }} />
            Visual Settings
          </Typography>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2,
              bgcolor: highContrastMode ? '#222' : 'background.paper',
              border: highContrastMode ? '1px solid #666' : 'none',
            }}
          >
            <FormControlLabel
              control={
                <Switch 
                  checked={highContrastMode} 
                  onChange={handleHighContrastChange} 
                  color="primary" 
                />
              }
              label="High Contrast Mode"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: highContrastMode ? 'white' : undefined,
                },
              }}
            />
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                ml: 4, 
                mb: 2,
                color: highContrastMode ? '#aaa' : undefined,
              }}
            >
              Enhances visibility with higher contrast colors for users with visual impairments.
            </Typography>
            
            <FormControlLabel
              control={
                <Switch 
                  checked={largeButtonsMode} 
                  onChange={handleLargeButtonsChange} 
                  color="primary" 
                />
              }
              label="Large Buttons Mode"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: highContrastMode ? 'white' : undefined,
                },
              }}
            />
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                ml: 4,
                color: highContrastMode ? '#aaa' : undefined,
              }}
            >
              Increases the size of buttons and text for easier interaction.
            </Typography>
          </Paper>
        </Grid>
        
        {/* Keyboard Shortcuts */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <KeyboardIcon sx={{ mr: 1 }} />
            Keyboard Shortcuts
          </Typography>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2,
              bgcolor: highContrastMode ? '#222' : 'background.paper',
              border: highContrastMode ? '1px solid #666' : 'none',
            }}
          >
            <Typography 
              variant="body2" 
              paragraph
              sx={{ 
                color: highContrastMode ? 'white' : undefined,
              }}
            >
              Use these keyboard shortcuts for faster calculator operation:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="body2" 
                  component="div"
                  sx={{ 
                    color: highContrastMode ? 'white' : undefined,
                  }}
                >
                  <strong>Basic Operations:</strong>
                  <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                    <li>Numbers: 0-9 keys</li>
                    <li>Addition: + key</li>
                    <li>Subtraction: - key</li>
                    <li>Multiplication: * key</li>
                    <li>Division: / key</li>
                    <li>Equals: Enter key</li>
                    <li>Clear: Escape key</li>
                    <li>Backspace: Backspace key</li>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography 
                  variant="body2" 
                  component="div"
                  sx={{ 
                    color: highContrastMode ? 'white' : undefined,
                  }}
                >
                  <strong>Scientific Functions:</strong>
                  <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                    <li>Power: ^ key</li>
                    <li>Decimal Point: . key</li>
                    <li>Tab: Navigate between elements</li>
                    <li>Shift+Tab: Navigate backwards</li>
                    <li>Space/Enter: Activate buttons</li>
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Accessibility Tips */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessibilityNewIcon sx={{ mr: 1 }} />
            Accessibility Tips
          </Typography>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2,
              bgcolor: highContrastMode ? '#222' : 'background.paper',
              border: highContrastMode ? '1px solid #666' : 'none',
            }}
          >
            <Typography 
              variant="body2" 
              paragraph
              sx={{ 
                color: highContrastMode ? 'white' : undefined,
              }}
            >
              <strong>Browser Zoom:</strong> You can use your browser's zoom feature (Ctrl/Cmd + Plus/Minus) to further adjust the size of the calculator.
            </Typography>
            <Typography 
              variant="body2" 
              paragraph
              sx={{ 
                color: highContrastMode ? 'white' : undefined,
              }}
            >
              <strong>Screen Readers:</strong> This calculator is compatible with screen readers. All buttons and inputs have appropriate ARIA labels.
            </Typography>
            <Typography 
              variant="body2"
              sx={{ 
                color: highContrastMode ? 'white' : undefined,
              }}
            >
              <strong>Keyboard Navigation:</strong> You can navigate through all calculator elements using the Tab key and operate them with Enter or Space.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Reset to Default Button */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button 
          variant="outlined" 
          onClick={() => {
            if (onHighContrastChange) onHighContrastChange(false);
            if (onLargeButtonsChange) onLargeButtonsChange(false);
            
            // Log reset to Firebase Analytics
            try {
              const analytics = getAnalytics();
              logEvent(analytics, 'accessibility_reset', {
                calculator_type: 'scientific',
              });
            } catch (error) {
              console.error('Analytics error:', error);
            }
          }}
          sx={{
            bgcolor: highContrastMode ? '#333' : undefined,
            color: highContrastMode ? 'white' : undefined,
            borderColor: highContrastMode ? '#666' : undefined,
            '&:hover': {
              bgcolor: highContrastMode ? '#444' : undefined,
              borderColor: highContrastMode ? '#999' : undefined,
            },
          }}
        >
          Reset to Default Settings
        </Button>
      </Box>
    </Box>
  );
};

export default AccessibilitySettings;
